import type { IContractRepository } from "@/application/ports/repositories";
import type { BulkResult } from "@/domain/types/bulk";
import type {
  ContractAggregates,
  ContractAggregatesParams,
  ContractDetails,
  ContractListParams,
  ContractListResponse,
  ContractParty,
  ContractSort,
  ContractStatsResponse,
  ResendDocumentResponse,
  VoidContractResponse,
} from "@/domain/contract";
import type { DocumentDownloadResponse } from "@/domain/documentBlocks";
import type { ExportDownloadOptions, ExportPreview } from "@/domain/export";
import { downloadBlob, downloadFromSignedUrl } from "../fileDownload";
import * as sdk from "../generated/sdk.gen";
import type { ContractDetailsResponse, ContractPartySummary } from "../generated/types.gen";
import { unwrap, unwrapBlobWithFilename } from "../unwrap";

const toSortQuery = (sort: readonly ContractSort[] | undefined) =>
  sort && sort.length > 0
    ? sort.map((entry) => `${entry.key}:${entry.direction}`).join(",")
    : undefined;

const toParty = (source: ContractPartySummary | null): ContractParty | null =>
  source
    ? {
        uuid: source.uuid,
        name: source.name,
        email: source.email,
        phone: source.phone,
        city: source.city,
        primary_contact: source.primary_contact,
      }
    : null;

const toDetails = (response: ContractDetailsResponse): ContractDetails => ({
  document_uuid: response.document.uuid,
  document_name: response.document.name,
  contract_type: response.contract_type,
  signature_status: response.signature.status,
  envelope_id: response.signature.envelope_id,
  sent_at: response.signature.sent_at,
  completed_at: response.signature.completed_at,
  voided_at: response.voided_at ?? null,
  signers: response.signature.signers ?? [],
  total_signers: response.signature_progress.total,
  signed_count: response.signature_progress.signed,
  investment_uuid: response.investment.uuid,
  investor: toParty(response.investment.investor),
  beneficiary: toParty(response.investment.beneficiary),
});

export class ContractRepository implements IContractRepository {
  async listContracts(
    params: ContractListParams = {},
    signal?: AbortSignal,
  ): Promise<ContractListResponse> {
    const { sort, ...rest } = params;
    const { items, ...page } = await unwrap(
      sdk.listContracts({ query: { ...rest, sort: toSortQuery(sort) }, signal }),
    );
    return { contracts: items, ...page };
  }

  async getContractAggregates(
    params: ContractAggregatesParams,
    signal?: AbortSignal,
  ): Promise<ContractAggregates> {
    const { aggregates, ...filters } = params;
    const response = await unwrap(
      sdk.getContractAggregates({
        query: { ...filters, aggregates: aggregates.join(",") },
        signal,
      }),
    );
    return response.aggregates;
  }

  async getContract(contractUuid: string, signal?: AbortSignal): Promise<ContractDetails> {
    return toDetails(
      await unwrap(sdk.getContractDetails({ path: { contract_uuid: contractUuid }, signal })),
    );
  }

  async getContractStats(params: ContractListParams = {}): Promise<ContractStatsResponse> {
    const { sort: _sort, limit: _limit, offset: _offset, ...filters } = params;
    return unwrap(sdk.getContractStats({ query: filters }));
  }

  async getContractDocument(
    contractUuid: string,
    signal?: AbortSignal,
  ): Promise<DocumentDownloadResponse> {
    return unwrap(sdk.getContractDocument({ path: { contract_uuid: contractUuid }, signal }));
  }

  async downloadContractDocument(contractUuid: string, fileName: string): Promise<void> {
    const document = await this.getContractDocument(contractUuid);
    await downloadFromSignedUrl(document.signed_url, fileName);
  }

  async previewExport(params: ContractListParams): Promise<ExportPreview> {
    const { sort: _sort, limit: _limit, offset: _offset, ...filters } = params;
    return unwrap(sdk.previewContractsExport({ query: filters }));
  }

  async downloadExport(params: ContractListParams, options: ExportDownloadOptions): Promise<void> {
    const { sort, limit: _limit, offset: _offset, ...filters } = params;
    const { blob, filename } = await unwrapBlobWithFilename(
      sdk.exportContracts,
      {
        query: {
          ...filters,
          sort: toSortQuery(sort),
          format: options.format,
          columns: options.columns.length > 0 ? options.columns.join(",") : undefined,
        },
      },
      `contracts.${options.format}`,
    );
    downloadBlob(blob, filename);
  }

  async resendContract(
    investmentUuid: string,
    documentUuid: string,
  ): Promise<ResendDocumentResponse> {
    return unwrap(
      sdk.resendDocument({
        path: { investment_uuid: investmentUuid, document_uuid: documentUuid },
      }),
    );
  }

  async bulkResendContracts(contractUuids: readonly string[]): Promise<BulkResult> {
    return unwrap(sdk.bulkResendContracts({ body: { contract_uuids: [...contractUuids] } }));
  }

  async voidContract(investmentUuid: string, documentUuid: string): Promise<VoidContractResponse> {
    return unwrap(
      sdk.voidContract({
        path: { investment_uuid: investmentUuid },
        body: { document_uuid: documentUuid },
      }),
    );
  }

  async deleteContract(investmentUuid: string, documentUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteInvestmentDocument({
        path: { investment_uuid: investmentUuid, document_uuid: documentUuid },
      }),
    );
  }
}
